// ============================================================
// OnPageProton WASM React 集成
// 交付文件: onpageproton.wasm + wasm_exec.js + 本文件
// ============================================================

// ---- 输入类型 ----

export interface JsonLdInput {
  has_breadcrumb?: boolean;
  has_article?: boolean;
  has_faq?: boolean;
  has_product?: boolean;
  has_shipping?: boolean;
  has_return?: boolean;
  has_review?: boolean;
}

export interface SemanticKeyword {
  keyword: string;
  keywords_score: number;
  headings_df_min: number;
  headings_df_max: number;
  body_df_min: number;
  body_df_max: number;
  df: number;
}

export interface ScoreInput {
  source_type?: "article" | "product" | "collection" | "page";
  target_keyword?: string;
  title?: string;
  content?: string;
  meta_title?: string;
  meta_description?: string;
  url?: string;
  domain?: string;
  language?: string;
  words_min?: number;
  words_max?: number;
  headings_min?: number;
  headings_max?: number;
  paragraphs_avg?: number;
  semantic_keywords?: SemanticKeyword[] | null;
  json_ld?: JsonLdInput;
  is_heading?: number;
  /** 是否已执行关键词分析；缺省 false。空 semantic_keywords 时 true 给满分、false 给 0 */
  is_analysis?: boolean;
  keyword_type_match?: boolean;
  product_tags?: string[];
  /** @deprecated 使用 json_ld.has_article；Go 端灰度 OR 合并 */
  has_article?: boolean;
  /** @deprecated 使用 json_ld.has_faq；Go 端灰度 OR 合并 */
  has_faq?: boolean;
}

// ---- 输出类型 ----

export interface MetricDetail {
  value: number;
  min?: number;
  max?: number;
  avg?: number;
  score: number;
}

export interface StructureDetail {
  word_count: MetricDetail;
  headings_count: MetricDetail;
  paragraphs_count: MetricDetail;
}

export interface CoverageDetail {
  matched: number;
  total: number;
  max_score: number;
  score: number;
}

export interface KeywordDetail {
  mode: "split" | "merged" | "single";
  total_coverage: CoverageDetail;
  title_coverage?: CoverageDetail;
}

export interface SemanticKWOut {
  keyword: string;
  keywords_score: number;
  headings_df_min: number;
  headings_df_max: number;
  body_df_min: number;
  body_df_max: number;
  df: number;
  title_matched: number;
  total_matched: number;
}

export interface ChecklistItem {
  key: string;
  passed: boolean;
  deduction: number;
  message: string;
}

export interface ScoreOutput {
  source_type: string;
  score: number;
  structure_score: number;
  keyword_score: number;
  seo_checklist_score: number;
  structure_detail: StructureDetail;
  keyword_detail: KeywordDetail;
  semantic_keywords: SemanticKWOut[];
  checklist_items: ChecklistItem[];
}

export interface ProtonError {
  code: "INVALID_INPUT" | "PARSE_FAILED" | "CALC_FAILED" | "INTERNAL_ERROR";
  message: string;
}

export type CalculateResult = ScoreOutput | { error: ProtonError };

declare class Go {
  importObject: WebAssembly.Imports;
  run(instance: WebAssembly.Instance): Promise<void>;
}

declare global {
  function calculateScore(inputJSON: string): string;
}

import { useState, useEffect, useCallback, useRef } from "react";

interface UseOnPageProtonOptions {
  wasmUrl: string;
  wasmExecLoaded?: boolean;
  wasmExecUrl?: string;
}

interface UseOnPageProtonReturn {
  ready: boolean;
  error: Error | null;
  calculate: (input: ScoreInput) => CalculateResult | null;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

export function useOnPageProton({
  wasmUrl,
  wasmExecLoaded = false,
  wasmExecUrl,
}: UseOnPageProtonOptions): UseOnPageProtonReturn {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    (async () => {
      try {
        if (!wasmExecLoaded) {
          const execUrl =
            wasmExecUrl || wasmUrl.replace(/[^/]+$/, "wasm_exec.js");
          await loadScript(execUrl);
        }

        const go = new Go();
        const result = await WebAssembly.instantiateStreaming(
          fetch(wasmUrl),
          go.importObject,
        );
        go.run(result.instance);
        setReady(true);
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    })();
  }, [wasmUrl, wasmExecLoaded, wasmExecUrl]);

  const calculate = useCallback(
    (input: ScoreInput): CalculateResult | null => {
      if (!ready) return null;
      const raw = globalThis.calculateScore(JSON.stringify(input));
      return JSON.parse(raw);
    },
    [ready],
  );

  return { ready, error, calculate };
}
